import { library } from '@fortawesome/fontawesome-svg-core'
import {faAlignJustify, faBook, faEdit ,faFile, faPlusSquare, faSync, } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
library.add(faAlignJustify, faBook, faSync, faFile, faPlusSquare, faEdit);

import * as React from 'react';
import FileEditor from "../FileEditor";
import JsonEditor from '../JsonEditor';
import { TemplateHolder } from '../logic';
import { MainComposer } from '../logic/composer';
import MainFileEditorController from '../logic/fileeditor';
import MetaData from '../logic/metadata';
import { MimeType } from '../logic/mimeTypes';
import { bufferToString, stringToArrayBuffer } from '../logic/util';
import Templates from '../Templates';

import './style.css';

interface IComposerProps {
  getFilecache: () => Templates;
}

interface IComposerState {
  tab: string;
  calculated: any;
}
const appendFileTypes = "pdf, docx, docm, xlsx, pptx, pptm, html, md, txt, gif, jpeg, jpg, png, svg, webp, bmp, msbmp";
const templateFileTypes = "docx, docm, xlsx, pptx, pptm, html, md, txt, csv";
const subtemplateFileTypes = "docx";

class Composer extends React.Component<IComposerProps, IComposerState> {
  public holder = new TemplateHolder();
  protected prependFiles: Templates;
  protected appendFiles: Templates;
  protected templates: Templates;
  protected template: Templates;
  protected data: Templates;
  protected working = false;
  constructor(props: any, context: any) {
    super(props, context);
    this.state = { tab: 'files', calculated: {} };
    this.holder.onChanged = () => {
      this.forceUpdate();
      this.setState({ calculated: MainComposer.calculate() });
    };
    this.calculateAll = this.calculateAll.bind(this);
    this.injectFilesData = this.injectFilesData.bind(this);
    this.exportJson = this.exportJson.bind(this);
    this.exportMeta =this.exportMeta.bind(this);
  }
  public componentDidMount() {
    MainComposer.onChange(calculated => this.setState({ calculated }));
    MainFileEditorController.onChange(() => this.forceUpdate());
    this.prependFiles.onChange(this.injectFilesData);
    this.appendFiles.onChange(this.injectFilesData);
    this.template.onChange(() => {
        this.injectFilesData();
        const temp = MainComposer.getMainTemplate();
        if(temp && (temp.mimeType === MimeType.html || temp.mimeType === MimeType.md)) this.editTemplate(temp.mimeType);
    });
    this.templates.onChange(this.injectFilesData);
    this.data.onChange(() => {
      const data = this.data.getTemplates()[0];
      if (!data) return;
      this.data.clear();
      if (!('buffer' in data)) {
        console.error('No buffer in data'); // TO DO
        return;
      }
      try {
        const actualData = JSON.parse(bufferToString(data.buffer));
        if (Array.isArray(actualData)) {
          MainComposer.files = actualData;
        } else {
          MainComposer.files.push({ data: actualData });
        }
        this.injectFilesData();
      } catch (e) {
        console.error(e); // TO DO
      }
    });
    this.calculateAll();
  }
  public calculateAll() {
    MainComposer.clear(true);
    this.injectFilesData();
  }
  public clear() {
    this.prependFiles.clear();
    this.appendFiles.clear();
    this.templates.clear();
    this.template.clear();
    MainComposer.clear();
    this.injectFilesData();
  }
  public injectFilesData() {
    if (this.working) return;
    this.working = true;
    MainComposer.prepend = this.prependFiles.getTemplates();
    MainComposer.append = this.appendFiles.getTemplates();
    MainComposer.templates = this.templates.getTemplates();
    const mainTemplate = this.template.getTemplates()[0];
    MainComposer.setMainTemplate(mainTemplate);
    this.setState({ calculated: MainComposer.calculate() });
    if (MainFileEditorController.getTemplate() !== mainTemplate) {
      MainFileEditorController.stopEditing();
    }
    this.working = false;
  }
  public extractFilesData() {
    this.working = true;
    this.prependFiles.setTemplates(MainComposer.prepend);
    this.appendFiles.setTemplates(MainComposer.append);
    this.templates.setTemplates(MainComposer.templates);
    const temp = MainComposer.getMainTemplate();
    this.template.setTemplates(temp ? [temp] : []);
    this.setState({ calculated: MainComposer.calculate() });
    this.working = false;
  }
  public render() {
    const mainTemplate = this.template && this.template.getTemplates()[0];
    let editTemplate;
    if (!mainTemplate) {
      editTemplate = <React.Fragment>
        <button className="info" onClick={this.editHtml}><FontAwesomeIcon icon="plus-square"/> Add HTML</button>
        <button className="info" onClick={this.editMd}><FontAwesomeIcon icon="plus-square"/>  Add Markdown</button>
      </React.Fragment>;
    }
    return (
      <div className="Composer">
        <div className="tabSelector">
          <button onClick={this.switchTab('files')} className={this.active('files')}>Files / Templates</button>
          <button onClick={this.switchTab('data')} className={this.active('data')}>Data</button>
          <button onClick={this.switchTab('check')} className={this.active('check')}>Check JSON</button>
        </div>
        <div className="tabs">
          <div className={`files ${this.active('files')}`}>
            <p>Drag in all required files</p>
            <Templates header="Prepend files" name="prepend files" supportedTypes={appendFileTypes} ref={r => this.prependFiles = r!} />
            <Templates header="Template" name="template" supportedTypes={templateFileTypes} ref={r => this.template = r!} single={true}>
              <div className="edit-template">{editTemplate}</div>
            </Templates>
            <FileEditor controller={MainFileEditorController} />
            <Templates header="Subtemplates" name="subtemplates" supportedTypes={subtemplateFileTypes} ref={r => this.templates = r!} />
            <Templates header="Append files" name="append files" supportedTypes={appendFileTypes} ref={r => this.appendFiles = r!} />
          </div>
          <div className={`data ${this.active('data')}`}>
            <p>Drag in a JSON file with data, or manually add data below</p>
            <Templates name="data" ref={r => this.data = r!} single={true} noRender={true} />
            <JsonEditor json={this.state.calculated.files || {}} onChange={this.setData} name="files" />
          </div>
          <div className={`check ${this.active('check')}`}>
            <button className="recalculate" onClick={this.calculateAll}>Recalculate JSON</button>
            <button className="export" onClick={this.exportJson}>Export JSON</button>
            <button className="export" onClick={this.exportMeta}>MetaData</button>
            <p>The data that'll be sent to the server can be previewed/modified here</p>
            <JsonEditor json={this.state.calculated || {}} onChange={this.setJson} />
          </div>
        </div>
      </div>
    );
  }
  protected editTemplate(mimeType: MimeType) {
    let template = this.template.getTemplates()[0];
    if (!template) {
      template = {
        mimeType,
        buffer: new ArrayBuffer(0),
        filename: 'Template',
      };
      this.template.addTemplate(template);
    }
    MainFileEditorController.startEditing(template);
  }
  protected exportJson() {
    const json = JSON.stringify(MainComposer.calculated);
    this.props.getFilecache().addTemplate({
      filename: 'Exported',
      mimeType: MimeType.json,
      buffer: stringToArrayBuffer(json),
    });
  }
  protected async exportMeta() {
    const json = JSON.stringify(MainComposer.template);
    this.props.getFilecache().addTemplate({
      filename: 'MetaData',
      mimeType: MimeType.json,
      buffer: stringToArrayBuffer(await MetaData.getMetaData(json)),
    });
  }
  protected active = (tab: string) => this.state.tab === tab ? 'active' : 'inactive';
  protected switchTab = (tab: string) => () => this.setState({ tab });
  protected setData = (data: any) => (MainComposer.files = data, this.injectFilesData());
  protected setJson = (json: any) => (MainComposer.set(json), this.extractFilesData());
  protected editHtml = () => this.editTemplate(MimeType.html);
  protected editMd = () => this.editTemplate(MimeType.md);
}

export default Composer;



// WEBPACK FOOTER //
// ./src/Composer/index.tsx